<script setup lang="ts">
import {computed, ref} from 'vue';
import Translator from "@/utils/common/Translator.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import NamaLabel from "@/components/NamaLabel.vue";
import ChatPresenter from "@/utils/presenters/ChatPresenter.ts";
import DTOChatParticipant = namaTypes.DTOChatParticipant;
import DialogCloseIcon from "@/assets/editViewIcons/DialogCloseIcon.vue";

const props = defineProps<{
  presenter: ChatPresenter;
  participants: DTOChatParticipant[];
}>();

const emit = defineEmits<{
  close: [];
}>();

const searchQuery = ref('');

const filteredParticipants = computed(() => {
  const search = searchQuery.value?.toLowerCase()?.trim();
  if (!search) return props.participants;
  return props.participants.filter(p => {
    const fields = [p.participant?.name1, p.participant?.name2, p.participant?.code];
    return fields.some(f => f?.toString().toLowerCase().includes(search));
  });
});
</script>

<template>
  <q-card class="tw-shadow-none dark:tw-text-white dark:tw-bg-namaBackground-deep-dark tw-rounded-2xl tw-bg-namaBackground-deep-light tw-w-fit">
    <q-bar v-if="Translator.translateId('chatParticipants')" class="tw-text-xs tw-text-primary-light dark:tw-text-primary-dark tw-h-10">
      <span class="tw-text-sm tw-font-semibold tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark"
            v-text="participants.length + ' ' + Translator.translateId('chatParticipants')"/>
      <q-space/>
      <q-avatar square @click="emit('close')" class="nama-avatar-hover tw-cursor-pointer">
        <DialogCloseIcon class="tw-size-4"/>
      </q-avatar>
    </q-bar>
  <div class="tw-flex tw-flex-col tw-max-h-[70vh] tw-h-[70vh] tw-w-80
              tw-bg-namaBackground-light dark:tw-bg-namaBackground-dark tw-rounded-lg tw-shadow-xl tw-overflow-hidden">
    <div class="tw-px-3 tw-py-2 tw-shrink-0">
      <input v-model="searchQuery"
             :placeholder="Translator.translateId('search')"
             class="tw-w-full tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded-md tw-px-3 tw-py-1.5 tw-text-sm
                    tw-bg-namaBackground-deep-light dark:tw-bg-namaBackground-deep-dark tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark
                    tw-outline-none focus:tw-border-primary-light dark:focus:tw-border-primary-dark"
             autofocus/>
    </div>
    <div class="tw-flex-1 tw-overflow-y-auto tw-px-1">
      <div v-if="filteredParticipants.length === 0"
           class="tw-text-center tw-py-6 tw-text-sm tw-text-gray-400"
           v-text="Translator.translateId('mentionNoResults')"/>
      <div v-else v-for="p in filteredParticipants" :key="p.participant?.id"
           class="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-rounded-md">
        <q-avatar size="36px" color="primary">
          <img :src="LoginUtils.getUserImageSrc(p.participant?.id ?? '', false)" alt="">
        </q-avatar>
        <div class="tw-flex-1 tw-min-w-0">
          <span class="tw-text-sm tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark tw-truncate tw-block"
                v-text="p.participant?.id === presenter.currentUserInfo?.id
                  ? Translator.translateId('You')
                  : Translator.translateRef(p.participant)"/>
          <div class="tw-flex tw-items-center tw-gap-1">
            <NamaIcon defaultIcon="circle" class="!tw-text-[6px]"
                      :class="[presenter.isUserOnline(p.participant?.id ?? '') ? 'active-green' : 'active-gray']"/>
            <NamaLabel :text="presenter.isUserOnline(p.participant?.id ?? '') ? 'activeNow' : 'notActive'"
                       class="!tw-text-xs"/>
          </div>
        </div>
        <span v-if="p.role === 'Admin' || presenter.currentChatMetadata.value?.groupOwner?.id === p.participant?.id"
              class="tw-text-[10px] tw-font-medium tw-text-primary-light dark:tw-text-primary-dark
                     tw-bg-primary-light/10 dark:tw-bg-primary-dark/10 tw-px-2 tw-py-0.5 tw-rounded-full">
          {{ p.participant?.id === presenter.currentChatMetadata.value?.groupOwner?.id
            ? Translator.translateId('chatGroupOwner')
            : Translator.translateId('chatGroupAdmin') }}
        </span>
      </div>
    </div>
  </div>
  </q-card>
</template>
